import { getApiBaseUrl } from '../config/api';

// FEAT-040 — Profile Completion Percentage API client (MVP-8.4,
// MVP-18.15).
//
// The caregiver-facing endpoint returns the full weighted breakdown so we
// can render the "next step" list with deep links. The public endpoint
// only exposes the rolled-up percentage so the badge on a viewer-facing
// caregiver profile cannot leak which specific items are still to come.

export type ProfileCompletionFieldKey =
  | 'bio'
  | 'avatar'
  | 'services'
  | 'availability'
  | 'credentialEntry'
  | 'acceptedCredentialDocuments'
  | 'completedLearningModules';

export interface ProfileCompletionBreakdownItem {
  key: ProfileCompletionFieldKey;
  label: string;
  weight: number;
  complete: boolean;
}

export interface CaregiverProfileCompletion {
  percentage: number;
  breakdown: ProfileCompletionBreakdownItem[];
}

export interface PublicProfileCompletion {
  percentage: number;
}

async function getAuthHeaders(): Promise<HeadersInit> {
  const token =
    typeof window !== 'undefined' ? window.localStorage.getItem('authToken') : null;
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function unwrap<T>(response: Response, fallback: string): Promise<T> {
  if (!response.ok) {
    let message = fallback;
    try {
      const body = await response.json();
      message = body.error || body.message || fallback;
    } catch {
      // ignore — keep fallback
    }
    throw new Error(message);
  }

  const body = await response.json();
  return body.data as T;
}

export const profileCompletionService = {
  async getOwnCompletion(): Promise<CaregiverProfileCompletion> {
    const baseUrl = await getApiBaseUrl();
    const response = await fetch(`${baseUrl}/caregivers/me/profile-completion`, {
      method: 'GET',
      headers: await getAuthHeaders(),
    });
    return unwrap<CaregiverProfileCompletion>(
      response,
      'Could not load your profile completion right now',
    );
  },

  async getPublicCompletion(caregiverId: string): Promise<PublicProfileCompletion> {
    const baseUrl = await getApiBaseUrl();
    const response = await fetch(
      `${baseUrl}/caregivers/${encodeURIComponent(caregiverId)}/profile-completion`,
      {
        method: 'GET',
        headers: await getAuthHeaders(),
      },
    );
    return unwrap<PublicProfileCompletion>(
      response,
      'Could not load profile completion right now',
    );
  },
};
